import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import {
  CaciStyles, PageHeader, DirectoryCard, SkelDirGrid, SectionNote,
  useReveal, useScrollProgress, useDebounced,
  excerptOf, truncate, langPath, localized, matches,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   CHAMBRE_DE_COMMERCES — the chamber network directory
   ───────────────────────────────────────────────────────────────────────────
   Same endpoint (/api/chambre_de_commerces), same detail route. Changes:

   · Search now ignores accents. Chamber names here are Algerian place names —
     Béjaïa, Tébessa, Aïn Témouchent — and a plain includes() meant typing
     "bejaia" returned nothing, which reads as a broken directory.
   · Slugs are URL-encoded. The link was built as
     `/chambre_de_commerce/${id}/${chambre.name}` with the raw name dropped
     straight into the path: spaces and accents produced malformed URLs, and
     any name containing a slash broke the route outright.
   · The loading spinner never ran — `isLoading` started false, so the page
     flashed "no data available" before the request returned. Real skeletons
     now, shaped like the cards that replace them.
   · A failed request no longer masquerades as an empty network.
   · The breadcrumb linked to a hard-coded "/", dropping Arabic and English
     visitors back onto the French homepage.
   · Search matched only the title. It now also reads the description, and
     reports how many of the total are showing.
   ═══════════════════════════════════════════════════════════════════════════ */

function Chambre_de_commerces() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [chambres, setChambres] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term, 250);

  const alive = useRef(true);
  const inputRef = useRef(null);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(() => {
    setState("loading");
    return axios
      .get(`${BASE_URL}/api/chambre_de_commerces`)
      .then((res) => {
        if (!alive.current) return;
        setChambres(Array.isArray(res.data) ? res.data : []);
        setState("ready");
      })
      .catch((err) => {
        if (!alive.current) return;
        console.error("Chambres: failed to load the directory —", err);
        setState("error");
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!debounced) return chambres;
    return chambres.filter((c) =>
      matches(localized(c, "name", lang), debounced) ||
      matches(localized(c, "description", lang), debounced)
    );
  }, [chambres, debounced, lang]);

  /* The detail route is /chambre_de_commerce/:id/:slug?. The slug segment is
     decorative, but it still has to be a legal path segment: encode it, and
     drop it entirely if there is nothing usable. */
  const linkFor = (c) => {
    const base = `/chambre_de_commerce/${c.id}`;
    const raw = c.slug || c.name;
    if (!raw) return base;
    const seg = encodeURIComponent(String(raw).trim());
    return seg ? `${base}/${seg}` : base;
  };

  const pageRef = useReveal([filtered, state]);
  const progress = useScrollProgress();

  const clearSearch = () => {
    setTerm("");
    inputRef.current && inputRef.current.focus();
  };

  const pageTitle = t("menu.chambres");
  const nf = lang === "ar" ? "ar-DZ" : lang === "en" ? "en-US" : "fr-FR";
  const showBar = state === "ready" && chambres.length > 8;

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />

      <Helmet>
        <title>{pageTitle} — {t("menu.caci")}</title>
        <meta name="description" content={t("home.chambres", pageTitle)} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={t("home.chambres", pageTitle)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={pageTitle}
        crumbs={[{ label: t("menu.home"), to: langPath(lang, "/") }, { label: pageTitle }]}
        count={
          state === "ready" && chambres.length > 0
            ? {
                value: debounced
                  ? `${filtered.length.toLocaleString(nf)} / ${chambres.length.toLocaleString(nf)}`
                  : chambres.length.toLocaleString(nf),
                label: pageTitle,
              }
            : null
        }
      />

      {showBar && (
        <div className="bar">
          <div className="caci-shell">
            <div className="bar-inner">
              <div className="field">
                <i className="bi bi-search" aria-hidden="true" />
                <input
                  ref={inputRef}
                  type="search"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={t("search.placeholder")}
                  aria-label={t("search.placeholder")}
                  autoComplete="off"
                  dir="auto"
                />
                {term && (
                  <button
                    type="button"
                    className="field-clear"
                    onClick={clearSearch}
                    aria-label={t("home.Close")}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="caci-section">
        <div className="caci-shell">
          {state === "loading" && <SkelDirGrid count={6} />}

          {state === "error" && (
            <SectionNote
              state="error"
              title={t("home.section_error_title", "Chargement impossible")}
              text={t("home.section_error", "Cette liste n'a pas pu être chargée.")}
              action={
                <button type="button" className="btn-quiet" onClick={load}>
                  <i className="bi bi-arrow-clockwise" />
                  {t("home.retry", "Réessayer")}
                </button>
              }
            />
          )}

          {state === "ready" && chambres.length === 0 && (
            <SectionNote text={t("home.no_data_available")} />
          )}

          {state === "ready" && chambres.length > 0 && filtered.length === 0 && (
            <SectionNote
              title={t("search.noResults")}
              text={t("search.tryDifferentTerms")}
              action={
                <button type="button" className="btn-quiet" onClick={clearSearch}>
                  <i className="bi bi-arrow-counterclockwise" />
                  {t("search.clear", "Tout effacer")}
                </button>
              }
            />
          )}

          {state === "ready" && filtered.length > 0 && (
            <div className="caci-grid">
              {filtered.map((c, i) => (
                <DirectoryCard
                  key={c.id ?? i}
                  index={i}
                  to={linkFor(c)}
                  logo={c.logo ? `${BASE_URL}/assets/entreprise/${c.logo}` : null}
                  title={truncate(localized(c, "name", lang), 70)}
                  description={excerptOf(localized(c, "description", lang), 22)}
                  actionLabel={t("home.visit")}
                  isRtl={isRtl}
                />
              ))}
            </div>
          )}

          <p className="caci-sr" aria-live="polite">
            {state === "ready" && debounced
              ? `${filtered.length} ${t("search.results", "résultats")}`
              : ""}
          </p>
        </div>
      </section>
    </div>
  );
}

export default Chambre_de_commerces;
